import { Link } from 'react-router-dom';
import PageShell, { PageTitle } from '../components/PageShell';
import CallButton from '../components/CallButton';
import HelpBox from '../components/HelpBox';
import ArticleList from '../components/ArticleList';
import FaqAccordion from '../components/FaqAccordion';
import { articlePath, articlesIn } from '../constants/articles';
import { FAQ_GROUPS } from '../constants/content';
import { IMAGES } from '../constants/images';

const FAQS = [
  ...FAQ_GROUPS.find((g) => g.id === 'sync').items,
  {
    q: 'Can I link one desktop tree to two Ancestry trees?',
    a: 'No. Each Family Tree Maker tree can be paired with a single Ancestry tree. If you need two online copies, you would have to keep two separate desktop trees.',
  },
  {
    q: 'Will my color coding appear on Ancestry after syncing?',
    a: 'Probably not. Color coding is a Family Tree Maker feature, so it stays on the desktop side even when everything else syncs.',
  },
  {
    q: 'Can I pull an existing Ancestry tree into Family Tree Maker?',
    a: 'Yes. From the Plan workspace you can download a tree from your Ancestry account, choose whether to bring media along, and decide whether it should stay synced afterwards.',
  },
];

const PROBLEMS = [
  ['Cannot sign in', 'Password mismatch, computer clock set wrong, or an out-of-date program', 'Test the login on the website, correct the date and time, then update FTM'],
  ['Sync never ends', 'Weak connection or lots of media to upload', 'Switch to a stable network and leave the program running'],
  ['Sync reports errors', 'Conflicting edits or security software blocking access', 'Back up, update, and allow Family Tree Maker through your firewall'],
  ['People appear twice', 'The same ancestor was added online and on the desktop', 'Back up first, then merge duplicates one pair at a time'],
  ['Hints have disappeared', 'Signed out, or hints turned off in settings', 'Sign in again and re-enable hints'],
];

const SERVICES = [
  {
    img: IMAGES.features.web,
    title: 'Ancestry & FamilySync',
    text: 'Keep a desktop tree and an online tree in step, and search Ancestry’s records from inside the program.',
    link: 'fix-familysync-errors',
  },
  {
    img: IMAGES.whatsNew.search,
    title: 'FamilySearch',
    text: 'Compare your people with a free worldwide tree and bring back the facts and sources you are missing.',
    link: 'familysearch-login-in-ftm',
  },
  {
    img: IMAGES.whatsNew.cloud,
    title: 'TreeVault',
    text: 'Store a copy of your trees online so a broken computer never means lost research.',
    link: 'treevault-cloud-services-guide',
  },
];

export default function AncestrySupportPage() {
  return (
    <PageShell>
      <PageTitle
        title="Ancestry Support"
        lead="Straightforward help with the online side of Family Tree Maker: Ancestry, FamilySearch, and TreeVault."
        crumbs={[{ label: 'Ancestry Support' }]}
      />

      <p>
        Family Tree Maker is at its best when it is connected. Ancestry brings in hints, record
        searches, and online trees. FamilySearch offers a free shared tree to compare against. TreeVault
        keeps a backup of your work in the cloud. The catch is that every connection adds something that
        can go wrong, from a forgotten password to a sync that will not finish.
      </p>
      <p>
        This page explains what each service does, how it talks to Family Tree Maker, and what to try
        when it stops cooperating. Questions about bills or subscriptions should go straight to the
        company that provides the service.
      </p>

      <CallButton />

      <section className="entry-section">
        <h2>The Three Services at a Glance</h2>
        <div className="img-grid">
          {SERVICES.map((item) => (
            <div key={item.title} className="img-card">
              <img src={item.img} alt="" width={640} height={427} loading="lazy" />
              <div className="img-card__body">
                <h3>{item.title}</h3>
                <p>
                  {item.text} <Link to={articlePath(item.link)}>Open the guide ›</Link>
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="entry-section">
        <h2>Understanding the Ancestry Connection</h2>
        <p>
          It helps to picture two separate places. Family Tree Maker is software on your computer.
          Ancestry is a website with online trees and an enormous collection of historical records. When
          you sign in to your Ancestry account from inside Family Tree Maker, the two can talk: hints
          appear on your people, the Web Search workspace can query Ancestry, and FamilySync can keep an
          online tree matched to your desktop one.
        </p>
        <h3>How FamilySync works</h3>
        <p>
          FamilySync pairs exactly one desktop tree with exactly one Ancestry tree. Changes you make at
          home can be sent up to the website, and anything added online can be brought down. Many
          researchers search records on Ancestry during the day and organize their findings in Family
          Tree Maker in the evening. If syncing stalls, our{' '}
          <Link to={articlePath('fix-familysync-errors')}>FamilySync troubleshooting guide</Link> is the
          place to start.
        </p>
        <h3>Making sense of hints</h3>
        <p>
          The little leaf icons are suggestions, not facts. Open each one, compare the names, dates, and
          places with what you already know, and only accept the ones that genuinely fit. Accepting hints
          in bulk is one of the fastest ways to fill a tree with the wrong people.
        </p>
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>FamilySearch and TreeVault Explained</h2>
        <h3>FamilySearch</h3>
        <p>
          FamilySearch is a free genealogy site built around a single tree that everyone shares. With a
          free account, Family Tree Maker can match your people to their FamilySearch profiles, show you
          where the two disagree, and let you copy across facts and sources. Our guide to{' '}
          <Link to={articlePath('familysearch-login-in-ftm')}>connecting FamilySearch</Link> covers the
          sign-in steps.
        </p>
        <h3>TreeVault</h3>
        <p>
          TreeVault is a cloud storage service offered to Family Tree Maker users. It keeps copies of your
          trees online, which is an excellent partner to the backups you make on a USB drive. Read{' '}
          <Link to={articlePath('treevault-cloud-services-guide')}>our TreeVault guide</Link> to set it up.
        </p>
      </section>

      <section className="entry-section">
        <h2>Troubleshooting Table</h2>
        <div className="table-wrap">
          <table className="data-table data-table--left">
            <thead>
              <tr>
                <th scope="col">Symptom</th>
                <th scope="col">Typical reason</th>
                <th scope="col">Try this first</th>
              </tr>
            </thead>
            <tbody>
              {PROBLEMS.map(([problem, cause, fix]) => (
                <tr key={problem}>
                  <td><strong>{problem}</strong></td>
                  <td>{cause}</td>
                  <td>{fix}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <HelpBox
        title="Sync Still Stuck?"
        text="Let a technician check your Ancestry link, sort out duplicates, and get FamilySync moving again."
      />

      <section className="entry-section">
        <h2>Changing or Closing an Account? Read This First</h2>
        <p>
          Cancelling a subscription, deleting an online tree, or closing an Ancestry account is final on
          the website side. Protect your work beforehand:
        </p>
        <ol>
          <li>Sync one last time so both copies of the tree are identical.</li>
          <li>Make a Family Tree Maker backup with media included, and copy it off the computer.</li>
          <li>For extra safety, export a GEDCOM file that almost any genealogy program can open.</li>
        </ol>
        <p>
          Details are in{' '}
          <Link to={articlePath('cancel-ancestry-keep-family-tree')}>what happens to your tree if you cancel Ancestry</Link>{' '}
          and <Link to={articlePath('delete-family-tree-on-ancestry')}>how to delete an online tree safely</Link>.
        </p>
      </section>

      <section className="entry-section">
        <h2>Tips for Trouble-Free Syncing</h2>
        <ul>
          <li>Sync little and often rather than letting months of changes pile up.</li>
          <li>Decide where you do most of your editing, desktop or website, and stick to it.</li>
          <li>Avoid editing the same person in both places between syncs.</li>
          <li>Keep Family Tree Maker updated; many sync problems disappear after an update.</li>
          <li>Back up before any large merge, import, or clean-up job.</li>
        </ul>
      </section>

      <section className="entry-section">
        <h2>Guides for Ancestry, FamilySearch & TreeVault</h2>
        <ArticleList articles={articlesIn('ancestry')} />
      </section>

      <CallButton />

      <section className="entry-section">
        <h2>Online Services: Questions and Answers</h2>
        <FaqAccordion items={FAQS} />
      </section>

      <CallButton />
    </PageShell>
  );
}
